import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import SEO from '../components/seo/SEO';
import Section from '../components/landing/Section';
import Badge from '../components/landing/Badge';
import FaqItem from '../components/landing/FaqItem';
import ProductShot from '../components/landing/ProductShot';
import LandingHeader from '../components/landing/LandingHeader';
import LandingFooter from '../components/landing/LandingFooter';
import SectionBackground from '../components/landing/SectionBackground';
import { buildHomeJsonLd } from '../constants/structuredData';
import { logEvent } from '../services/analytics';
import {
  SparklesIcon,
  PaintBrushIcon,
  CalendarIcon,
  ChartBarIcon,
  DevicePhoneMobileIcon,
  UsersIcon,
  CheckCircleIcon,
  ArrowRightIcon,
} from '../constants/icons';

const FAQ_KEYS = [
  'whatIsIt',
  'isItFree',
  'whichPlatforms',
  'whatAi',
  'teams',
  'schedule',
  'languages',
  'designSkills',
];

function PrimaryCta({ children, label }) {
  return (
    <Link
      to="/register"
      onClick={() => logEvent('Landing', 'cta_click', label)}
      className="btn-gradient inline-flex items-center justify-center gap-2 rounded-xl px-8 py-4 text-lg font-semibold text-white"
    >
      {children}
      <ArrowRightIcon className="h-5 w-5" />
    </Link>
  );
}

export default function Home() {
  const { t } = useTranslation();
  const { currentUser } = useAuth();

  const faqItems = FAQ_KEYS.map((key) => ({
    question: t(`home.faq.items.${key}.question`),
    answer: t(`home.faq.items.${key}.answer`),
  }));

  const featureList = [
    { key: 'aiCaptions', Icon: SparklesIcon, variant: 'live', shot: 'app-generate' },
    { key: 'customTemplates', Icon: PaintBrushIcon, variant: 'live', shot: 'app-templates' },
    { key: 'facebookPublish', Icon: CalendarIcon, variant: 'live', shot: null },
    { key: 'analytics', Icon: ChartBarIcon, variant: 'live', shot: 'app-analytics' },
  ];

  const steps = ['generate', 'brand', 'publish'];
  const stepIcons = { generate: SparklesIcon, brand: PaintBrushIcon, publish: CalendarIcon };

  const teamPoints = [
    { key: 'multiBrand', Icon: UsersIcon },
    { key: 'collaboration', Icon: UsersIcon },
    { key: 'consistency', Icon: CheckCircleIcon },
  ];

  return (
    <Layout>
      <SEO
        title={t('home.meta.title')}
        description={t('home.meta.description')}
        path="/"
        jsonLd={buildHomeJsonLd(faqItems)}
      />

      <LandingHeader />

      <div className="relative overflow-hidden">
        {/* Hero */}
        <div className="relative">
          <SectionBackground opacity={0.85} fade strong />
          <Section className="pt-16 pb-16 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent-primary mb-4">
              {t('home.hero.eyebrow')}
            </p>

            <h1 className="text-5xl md:text-6xl font-extrabold mb-6 leading-tight">
              <span className="gradient-text">{t('home.hero.title')}</span>
              <br />
              <span className="text-text-primary">{t('home.hero.subtitle')}</span>
            </h1>

            <p className="text-xl text-text-secondary mb-6 max-w-2xl mx-auto leading-relaxed">
              {t('home.hero.description')}
            </p>

            <div className="flex flex-wrap items-center justify-center gap-3 mb-8">
              <Badge variant="live">{t('home.hero.facebookLive')} · {t('home.badges.live')}</Badge>
              <Badge variant="soon">{t('home.hero.morePlatformsSoon')} · {t('home.badges.comingSoon')}</Badge>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              {currentUser ? (
                <Link
                  to="/home"
                  onClick={() => logEvent('Landing', 'cta_click', 'hero_dashboard')}
                  className="btn-gradient inline-flex items-center justify-center gap-2 rounded-xl px-8 py-4 text-lg font-semibold text-white"
                >
                  {t('home.hero.goToDashboard')}
                </Link>
              ) : (
                <PrimaryCta label="hero">{t('home.hero.ctaPrimary')}</PrimaryCta>
              )}
              <a
                href="#how-it-works"
                className="inline-flex items-center justify-center rounded-xl border border-border-primary bg-bg-primary px-8 py-4 text-lg font-semibold text-text-primary hover:bg-bg-tertiary transition-colors"
              >
                {t('home.hero.ctaSecondary')}
              </a>
            </div>
            {!currentUser && (
              <>
                <p className="mt-4 text-sm text-text-secondary">{t('home.hero.trustLine')}</p>
                <p className="mt-2 text-sm text-text-secondary">
                  {t('home.hero.alreadyHaveAccount')}{' '}
                  <Link
                    to="/login"
                    onClick={() => logEvent('Landing', 'login_click', 'hero')}
                    className="font-semibold text-accent-primary hover:underline"
                  >
                    {t('home.hero.login')}
                  </Link>
                </p>
              </>
            )}
          </div>
          </Section>
        </div>

        {/* Trust strip */}
        <Section title={t('home.trust.title')} className="py-10">
          <div className="flex flex-wrap justify-center gap-3">
            {Object.keys(t('home.trust.items', { returnObjects: true })).map((key) => (
              <span
                key={key}
                className="inline-flex items-center gap-2 rounded-full bg-bg-tertiary px-4 py-2 text-sm font-medium text-text-secondary"
              >
                <CheckCircleIcon className="h-4 w-4 text-accent-primary" />
                {t(`home.trust.items.${key}`)}
              </span>
            ))}
          </div>
        </Section>

        {/* How it works */}
        <Section
          id="how-it-works"
          title={t('home.howItWorks.title')}
          subtitle={t('home.howItWorks.subtitle')}
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {steps.map((step) => {
              const Icon = stepIcons[step];
              return (
                <div key={step} className="enterprise-card p-8 text-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-accent-primary to-accent-secondary rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                    <Icon className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-text-primary">
                    {t(`home.howItWorks.steps.${step}.title`)}
                  </h3>
                  <p className="text-text-secondary leading-relaxed">
                    {t(`home.howItWorks.steps.${step}.description`)}
                  </p>
                </div>
              );
            })}
          </div>
        </Section>

        {/* Feature deep-dive */}
        <Section title={t('home.features.title')} subtitle={t('home.features.subtitle')}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {featureList.map(({ key, Icon, variant, shot }) => (
              <div key={key} className="enterprise-card p-8">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 flex-shrink-0 bg-gradient-to-r from-accent-primary to-accent-secondary rounded-2xl flex items-center justify-center shadow-lg">
                    <Icon className="h-7 w-7 text-white" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-xl font-bold text-text-primary">
                        {t(`home.features.${key}.title`)}
                      </h3>
                      <Badge variant={variant}>{t('home.badges.live')}</Badge>
                    </div>
                    <p className="text-text-secondary leading-relaxed">
                      {t(`home.features.${key}.description`)}
                    </p>
                  </div>
                </div>
                {shot && (
                  <div className="mt-6">
                    <ProductShot name={shot} alt={t(`home.features.${key}.title`)} />
                  </div>
                )}
              </div>
            ))}

            {/* More platforms — coming soon */}
            <div className="enterprise-card p-8 md:col-span-2">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 flex-shrink-0 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center shadow-lg">
                  <DevicePhoneMobileIcon className="h-7 w-7 text-white" />
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-xl font-bold text-text-primary">
                      {t('home.hero.morePlatformsSoon')}
                    </h3>
                    <Badge variant="soon">{t('home.badges.comingSoon')}</Badge>
                  </div>
                  <p className="text-text-secondary leading-relaxed">
                    {t('home.features.facebookPublish.description')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Section>

        {/* Built for teams */}
        <div className="relative bg-bg-tertiary/60 border-y border-border-primary">
          <SectionBackground opacity={0.6} />
          <Section title={t('home.teams.title')} subtitle={t('home.teams.subtitle')} className="relative z-10">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-10">
              {teamPoints.map(({ key, Icon }) => (
                <div key={key} className="text-center">
                  <div className="w-14 h-14 bg-gradient-to-r from-accent-primary to-accent-secondary rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <Icon className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="text-lg font-bold mb-2 text-text-primary">
                    {t(`home.teams.points.${key}.title`)}
                  </h3>
                  <p className="text-text-secondary leading-relaxed">
                    {t(`home.teams.points.${key}.description`)}
                  </p>
                </div>
              ))}
            </div>
            {!currentUser && (
              <div className="text-center">
                <PrimaryCta label="teams">{t('home.hero.ctaPrimary')}</PrimaryCta>
              </div>
            )}
          </Section>
        </div>

        {/* Pricing */}
        <Section title={t('home.pricing.title')} subtitle={t('home.pricing.subtitle')}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Free */}
            <div className="enterprise-card p-8 border-2 border-accent-primary">
              <h3 className="text-2xl font-bold mb-2 text-text-primary">{t('home.pricing.free.title')}</h3>
              <div className="text-4xl font-bold gradient-text mb-2">{t('home.pricing.free.price')}</div>
              <p className="text-text-secondary mb-6">{t('home.pricing.free.subtitle')}</p>
              <ul className="text-left space-y-3 mb-8">
                {Object.keys(t('home.pricing.free.features', { returnObjects: true })).map((key) => (
                  <li key={key} className="flex items-center gap-2">
                    <CheckCircleIcon className="h-5 w-5 text-green-500 flex-shrink-0" />
                    <span className="text-text-primary">{t(`home.pricing.free.features.${key}`)}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/register"
                onClick={() => logEvent('Landing', 'cta_click', 'pricing_free')}
                className="btn-gradient block w-full rounded-xl px-6 py-3 text-center text-lg font-semibold text-white"
              >
                {t('home.pricing.free.button')}
              </Link>
            </div>

            {/* Pro — coming soon */}
            <div className="enterprise-card p-8 opacity-90">
              <div className="inline-block bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200 px-3 py-1 rounded-full text-xs font-semibold mb-4">
                {t('home.pricing.pro.badge')}
              </div>
              <h3 className="text-2xl font-bold mb-2 text-text-primary">{t('home.pricing.pro.title')}</h3>
              <div className="text-4xl font-bold gradient-text mb-2">{t('home.pricing.pro.price')}</div>
              <p className="text-text-secondary mb-6">{t('home.pricing.pro.subtitle')}</p>
              <ul className="text-left space-y-3 mb-8">
                {Object.keys(t('home.pricing.pro.features', { returnObjects: true })).map((key) => (
                  <li key={key} className="flex items-center gap-2">
                    <CheckCircleIcon className="h-5 w-5 text-green-500 flex-shrink-0" />
                    <span className="text-text-primary">{t(`home.pricing.pro.features.${key}`)}</span>
                  </li>
                ))}
              </ul>
              <button
                disabled
                className="w-full rounded-xl bg-bg-tertiary px-6 py-3 text-lg font-semibold text-text-secondary cursor-not-allowed"
              >
                {t('home.pricing.pro.button')}
              </button>
            </div>
          </div>
        </Section>

        {/* FAQ */}
        <Section title={t('home.faq.title')}>
          <div className="max-w-3xl mx-auto space-y-4">
            {faqItems.map((item) => (
              <FaqItem key={item.question} question={item.question} answer={item.answer} />
            ))}
          </div>
        </Section>

        {/* Final CTA */}
        {!currentUser && (
          <section className="px-4 py-16">
            <div className="relative max-w-5xl mx-auto rounded-3xl bg-gradient-to-r from-accent-primary to-accent-secondary p-12 text-center text-white overflow-hidden">
              {/* sparks layer (currentColor = white, low opacity) */}
              <div
                aria-hidden="true"
                className="absolute inset-0 -z-0 pointer-events-none text-white opacity-25"
                style={{
                  backgroundImage: `url("${process.env.PUBLIC_URL || ''}/backgrounds/circle-sparks-transparent.svg")`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
              />
              <div className="relative z-10">
                <h2 className="text-3xl md:text-4xl font-bold mb-4">{t('home.finalCta.title')}</h2>
                <p className="text-lg mb-8 opacity-90">{t('home.finalCta.subtitle')}</p>
                <Link
                  to="/register"
                  onClick={() => logEvent('Landing', 'cta_click', 'final')}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-white px-8 py-4 text-lg font-semibold text-accent-primary hover:opacity-90 shadow-lg"
                >
                  {t('home.finalCta.button')}
                  <ArrowRightIcon className="h-5 w-5" />
                </Link>
              </div>
            </div>
          </section>
        )}
      </div>

      <LandingFooter />
    </Layout>
  );
}
